"use client";

import { useEffect, useRef, useState } from "react";
import {
  motion,
  AnimatePresence,
  useMotionValue,
  useTransform,
  useReducedMotion,
  type PanInfo,
} from "framer-motion";
import {
  Icon20PlaceOutline,
  Icon28LikeOutline,
  Icon24Cancel,
} from "@linqx/icons/web";
import { Language } from "@linqx/i18n";
import { cn } from "@/lib/utils";

interface Profile {
  name: string;
  age: number;
  dist: string;
  vibes: string[];
  gradient: string;
  initial: string;
}

const PROFILES: Profile[] = [
  {
    name: "Maya",
    age: 26,
    dist: "1.2 km away",
    vibes: ["Coffee", "Live music", "Art"],
    gradient: "from-brand-300 via-brand-500 to-brand-900",
    initial: "M",
  },
  {
    name: "Leo",
    age: 29,
    dist: "0.8 km away",
    vibes: ["Nightlife", "Travel"],
    gradient: "from-brand-400 via-brand-600 to-brand-950",
    initial: "L",
  },
  {
    name: "Ava",
    age: 24,
    dist: "2.4 km away",
    vibes: ["Foodie", "Outdoor", "Coffee"],
    gradient: "from-brand-500 via-brand-700 to-brand-950",
    initial: "A",
  },
  {
    name: "Sam",
    age: 27,
    dist: "3.1 km away",
    vibes: ["Sports", "Adventure"],
    gradient: "from-brand-400 via-brand-500 to-brand-800",
    initial: "S",
  },
];

type Card = { profile: Profile; key: number };

/** Exit fling resolves off the swipe direction passed via AnimatePresence custom. */
const cardVariants = {
  exit: (d: number) => ({
    x: d * 640,
    rotate: d * 22,
    opacity: 0,
    transition: { duration: 0.35 },
  }),
};

/**
 * A draggable Tinder-style profile deck. The front card can be flung left/right
 * (or auto-advances when idle), throwing it off-screen while the next rises.
 * The deck is infinite — swiped cards loop back to the bottom.
 */
export function SwipeDeck({ className }: { className?: string }) {
  const reduce = useReducedMotion();
  const [cards, setCards] = useState<Card[]>(() =>
    PROFILES.map((profile, i) => ({ profile, key: i })),
  );
  const [dir, setDir] = useState(1);
  const [paused, setPaused] = useState(false);
  const keyRef = useRef(PROFILES.length);

  const swipe = (d: 1 | -1) => {
    setDir(d);
    setCards((cs) => {
      if (cs.length === 0) return cs;
      const [first, ...rest] = cs;
      return [...rest, { profile: first.profile, key: keyRef.current++ }];
    });
  };

  // Idle auto-advance keeps the deck alive without interaction.
  useEffect(() => {
    if (reduce || paused) return;
    const id = setInterval(() => swipe(1), 4200);
    return () => clearInterval(id);
  }, [reduce, paused]);

  const visible = cards.slice(0, 3);

  return (
    <div
      className={cn("flex flex-col items-center gap-6", className)}
      onPointerEnter={() => setPaused(true)}
      onPointerLeave={() => setPaused(false)}
    >
      <div className="relative h-[430px] w-[300px]">
        <AnimatePresence custom={dir}>
          {visible.map((card, depth) => (
            <SwipeCard
              key={card.key}
              profile={card.profile}
              depth={depth}
              isFront={depth === 0}
              draggable={!reduce}
              custom={dir}
              onSwipe={swipe}
            />
          ))}
        </AnimatePresence>
      </div>

      <div className="flex items-center gap-5">
        <button
          aria-label={Language.getKey("landing_deck_pass_label")}
          onClick={() => swipe(-1)}
          className="flex h-12 w-12 items-center justify-center rounded-full border border-brand-200 bg-white text-brand-400 shadow-sm transition-transform hover:scale-110 hover:text-brand-600 active:scale-95"
        >
          <Icon24Cancel className="h-5 w-5" />
        </button>
        <button
          aria-label={Language.getKey("landing_deck_link_label")}
          onClick={() => swipe(1)}
          className="flex h-14 w-14 items-center justify-center rounded-full bg-gradient-to-br from-brand-900 to-brand-500 text-white shadow-lg shadow-brand-500/40 transition-transform hover:scale-110 active:scale-95"
        >
          <Icon28LikeOutline className="h-6 w-6" />
        </button>
      </div>
    </div>
  );
}

function SwipeCard({
  profile,
  depth,
  isFront,
  draggable,
  custom,
  onSwipe,
}: {
  profile: Profile;
  depth: number;
  isFront: boolean;
  draggable: boolean;
  custom: number;
  onSwipe: (d: 1 | -1) => void;
}) {
  // Drag position drives rotation + stamp opacity. framer owns the x transform
  // (via `drag`), so the exit fling animates cleanly.
  const dragX = useMotionValue(0);
  const rotate = useTransform(dragX, [-220, 220], [-15, 15]);
  const likeOpacity = useTransform(dragX, [30, 130], [0, 1]);
  const nopeOpacity = useTransform(dragX, [-130, -30], [1, 0]);

  const handleDragEnd = (_: unknown, info: PanInfo) => {
    dragX.set(0);
    if (info.offset.x > 110 || info.velocity.x > 700) onSwipe(1);
    else if (info.offset.x < -110 || info.velocity.x < -700) onSwipe(-1);
  };

  return (
    <motion.div
      className="absolute inset-0"
      style={{ rotate: isFront ? rotate : 0, zIndex: 30 - depth }}
      custom={custom}
      variants={cardVariants}
      initial={{ scale: 0.86, y: 46, opacity: 0 }}
      animate={{ scale: 1 - depth * 0.05, y: depth * 16, opacity: 1 }}
      exit="exit"
      transition={{ type: "spring", stiffness: 320, damping: 30 }}
      drag={isFront && draggable ? "x" : false}
      dragSnapToOrigin
      dragElastic={0.7}
      onDrag={(_, info) => dragX.set(info.offset.x)}
      onDragEnd={handleDragEnd}
      whileTap={isFront ? { cursor: "grabbing" } : undefined}
    >
      <div
        className={cn(
          "relative h-full w-full overflow-hidden rounded-[2rem] bg-gradient-to-br shadow-xl shadow-brand-900/20",
          profile.gradient,
          isFront && draggable ? "cursor-grab" : "",
        )}
      >
        <div className="absolute -right-8 -top-10 h-44 w-44 rounded-full bg-white/25 blur-2xl" />
        <div className="absolute bottom-28 left-6 h-24 w-24 rounded-full bg-brand-950/25 blur-2xl" />

        {/* Monogram watermark */}
        <div className="absolute right-5 top-4 font-heading text-6xl font-black text-white/15">
          {profile.initial}
        </div>

        {/* Stamps */}
        {isFront ? (
          <>
            <motion.div
              style={{ opacity: likeOpacity }}
              className="absolute left-5 top-6 -rotate-12 rounded-xl border-2 border-white px-3 py-1 text-sm font-extrabold tracking-widest text-white"
            >
              {Language.getKey("landing_deck_stamp_link")}
            </motion.div>
            <motion.div
              style={{ opacity: nopeOpacity }}
              className="absolute right-5 top-6 rotate-12 rounded-xl border-2 border-white/80 px-3 py-1 text-sm font-extrabold tracking-widest text-white/80"
            >
              {Language.getKey("landing_deck_stamp_nope")}
            </motion.div>
          </>
        ) : null}

        <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent p-5">
          <div className="font-heading text-2xl font-bold text-white">
            {profile.name}, {profile.age}
          </div>
          <div className="mt-0.5 flex items-center gap-1 text-xs text-white/85">
            <Icon20PlaceOutline className="h-3.5 w-3.5" />
            {profile.dist}
          </div>
          <div className="mt-2.5 flex flex-wrap gap-1.5">
            {profile.vibes.map((v) => (
              <span
                key={v}
                className="rounded-full bg-white/20 px-2.5 py-0.5 text-[11px] font-semibold text-white backdrop-blur-sm"
              >
                {v}
              </span>
            ))}
          </div>
        </div>
      </div>
    </motion.div>
  );
}
